import React, { useState } from "react";
import axios from "axios";
import { Form, Button, Container, Spinner } from "react-bootstrap";
import { useNavigate } from "react-router-dom";

const InvoiceForm = ({
  CustomerId,
  customerName,
  setModalShow,
  setRefresh,
  setErrMsg,
}) => {
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    CustomerId: CustomerId,
    invoicePrefix: customerName.slice(0, 3).toUpperCase(),
    invoiceNumber: "",
    date: "",
    dueDate: "", // will be saved as dd/mm/yyyy
    desc: "",
    items: [{ description: "", units: "", unitPrice: "" }],
    total: 0,
  });

  // 🔹 Convert YYYY-MM-DD to dd/mm/yyyy
  const formatToDDMMYYYY = (dateObj) => {
    const day = String(dateObj.getDate()).padStart(2, "0");
    const month = String(dateObj.getMonth() + 1).padStart(2, "0");
    const year = dateObj.getFullYear();
    return `${day}/${month}/${year}`;
  };

  // 🔹 Add 7 days safely (no timezone shift)
  const addDaysFormatted = (dateValue, days) => {
    if (!dateValue) return "";

    const date = new Date(dateValue + "T00:00:00");
    date.setDate(date.getDate() + days);

    return formatToDDMMYYYY(date);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "date") {
      setFormData((prev) => ({
        ...prev,
        date: value,
        dueDate: addDaysFormatted(value, 7),
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleItemChange = (index, e) => {
    const newItems = [...formData.items];
    newItems[index][e.target.name] = e.target.value;
    setFormData({ ...formData, items: newItems });
  };

  const addItem = () => {
    setFormData({
      ...formData,
      items: [...formData.items, { description: "", units: "", unitPrice: "" }],
    });
  };

  const removeItem = (index) => {
    const newItems = formData.items.filter((_, i) => i !== index);
    setFormData({ ...formData, items: newItems });
  };

  const calculateTotal = () => {
    return formData.items.reduce((total, item) => {
      const units = parseFloat(item.units) || 0;
      const price = parseFloat(item.unitPrice) || 0;
      return total + units * price;
    }, 0);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const token = localStorage.getItem("token");
      const total = calculateTotal();

      await axios.post(
        `${process.env.REACT_APP_API_URL}/invoices/create`,
        { ...formData, total },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      setLoading(false);
      setModalShow(false);
      setRefresh(true);
      navigate(`/customer/${CustomerId}`);
    } catch (error) {
      setErrMsg(
        "Something went wrong. Please contact support and provide this error: '" +
          error.message +
          "'",
      );
      setLoading(false);
      navigate(`/customer/${CustomerId}`);
      setRefresh(true);
    }
  };

  if (loading) {
    return (
      <Container
        style={{ height: "100vh", fontSize: "250px" }}
        className="d-flex justify-content-center align-items-center"
      >
        <Spinner animation="border" role="status" />
      </Container>
    );
  }

  return (
    <Container style={{ maxWidth: "600px" }}>
      <h1>Create Invoice</h1>

      <Form onSubmit={handleSubmit}>
        <Form.Group>
          <Form.Label className="fw-bold mt-2">Customer</Form.Label>
          <Form.Control type="text" value={customerName} readOnly required />
        </Form.Group>

        <Form.Group>
          <Form.Label className="fw-bold mt-2">Date</Form.Label>
          <Form.Control
            type="date"
            name="date"
            value={formData.date}
            onChange={handleChange}
            required
          />
        </Form.Group>

        <Form.Group>
          <Form.Label className="fw-bold mt-2">Due Date</Form.Label>
          <Form.Control
            type="text"
            name="dueDate"
            value={formData.dueDate}
            readOnly
          />
        </Form.Group>

        <Form.Group>
          <Form.Label className="fw-bold mt-2">Invoice Number</Form.Label>
          <Form.Control
            type="text"
            name="invoiceNumber"
            value={formData.invoiceNumber}
            onChange={handleChange}
            placeholder="Leave blank for auto number"
          />
        </Form.Group>

        <Form.Group>
          <Form.Label className="fw-bold mt-2">Invoice Description</Form.Label>
          <Form.Control
            as="textarea"
            rows={3}
            name="desc"
            value={formData.desc}
            onChange={handleChange}
            required
          />
        </Form.Group>

        {formData.items.map((item, index) => (
          <div key={index} style={{ marginBottom: "1rem" }}>
            <Form.Group>
              <Form.Label className="fw-bold mt-2">Item Description</Form.Label>
              <Form.Control
                type="text"
                name="description"
                value={item.description}
                onChange={(e) => handleItemChange(index, e)}
                required
              />
            </Form.Group>

            <Form.Group>
              <Form.Label className="fw-bold mt-2">Units</Form.Label>
              <Form.Control
                type="number"
                step="any"
                name="units"
                min={0}
                value={item.units}
                onChange={(e) => handleItemChange(index, e)}
                required
              />
            </Form.Group>

            <Form.Group>
              <Form.Label className="fw-bold mt-2">Unit Price</Form.Label>
              <Form.Control
                type="number"
                step="any"
                name="unitPrice"
                value={item.unitPrice}
                onChange={(e) => handleItemChange(index, e)}
                required
              />
            </Form.Group>

            {index > 0 && (
              <Button
                variant="danger"
                className="mt-2"
                onClick={() => removeItem(index)}
              >
                Remove Item
              </Button>
            )}
          </div>
        ))}

        <Button variant="secondary" className="mb-3" onClick={addItem}>
          Add Item
        </Button>

        <Form.Group>
          <Form.Label>Total</Form.Label>
          <Form.Control type="text" value={calculateTotal()} readOnly />
        </Form.Group>

        <Button className="mt-3" type="submit">
          Create Invoice
        </Button>
      </Form>
    </Container>
  );
};

export default InvoiceForm;
